import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaFilter, FaBoxes, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const AdminProducts = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  
  // Refs for scrolling
  const formRef = useRef(null);
  const tableRef = useRef(null);

  const [formData, setFormData] = useState({
    category: '',
    brand: '',
    product_name: '',
    description: '',
    price_single: '',
    price_box: '',
    price_packet: '',
    box_size: '',
    currency: 'EGP',
    stock_single: '',
    stock_box: '',
    stock_packet: '',
    images: ['', '']
  });

  // Category to brands mapping
  const categoryBrands = {
    "Cuban": [
      "Cohiba",
      "Trinidad",
      "Montecristo",
      "Partagas",
      "Romeo Y Julieta",
      "H.Upmann",
      "Hoyo de Monterrey",
      "Quintero",
      "Jose L.Piedra"
    ],
    "Non-Cuban": [
      "Olivia",
      "Camacho",
      "Drew Estate Liga Privada",
      "AJ Fernandez",
      "Gurkha",
      "Perdomo",
      "Alec Bradley",
      "Plasencia",
      "Foundation",
      "Davidoff",
      "E.P. Carillo",
      "Rocky Patel",
      "Montecristo",
      "Diesel",
      "La Aurora",
      "El Septimo",
      "Arturo Fuente"
    ],
    "Cigarillo": [
      "Cohiba",
      "Montecristo",
      "Partagas",
      "Romeo Y Julieta",
      "Trinidad",
      "Davidoff",
      "Toscano Italy"
    ]
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.PRODUCTS.LIST, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "category") {
      setFormData({
        ...formData,
        category: value,
        brand: "" // reset brand when category changes
      });
    } else if (name.startsWith('image')) {
      const idx = name === 'image1' ? 0 : 1;
      const newImages = [...formData.images];
      newImages[idx] = value;
      setFormData({ ...formData, images: newImages });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that at least one price is provided
    const hasPrice = formData.price_single || formData.price_box || formData.price_packet;
    if (!hasPrice) {
      alert(t('admin.provideAtLeastOnePrice'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Prepare data for submission - convert empty strings to null for optional fields
      const submitData = {
        ...formData,
        price_single: formData.price_single ? parseFloat(formData.price_single) : null,
        price_box: formData.price_box ? parseFloat(formData.price_box) : null,
        price_packet: formData.price_packet ? parseFloat(formData.price_packet) : null,
        stock_single: formData.stock_single ? parseInt(formData.stock_single) : 0,
        stock_box: formData.stock_box ? parseInt(formData.stock_box) : 0,
        stock_packet: formData.stock_packet ? parseInt(formData.stock_packet) : 0,
        box_size: formData.box_size ? parseInt(formData.box_size) : 1,
        images: formData.images.filter(Boolean)
      };

      // Remove the old stock field if it exists
      delete submitData.stock;

      console.log('Submitting data:', submitData); // Debug log

      if (editingProduct) {
        // Update existing product
        await axios.put(API_ENDPOINTS.PRODUCTS.UPDATE(editingProduct._id), submitData, { headers });
        setEditingProduct(null);
      } else {
        // Create new product
        await axios.post(API_ENDPOINTS.PRODUCTS.CREATE, submitData, { headers });
      }

      // Reset form and refresh products
      setFormData({
        category: '',
        brand: '',
        product_name: '',
        description: '',
        price_single: '',
        price_box: '',
        price_packet: '',
        box_size: '',
        currency: 'EGP',
        stock_single: '',
        stock_box: '',
        stock_packet: '',
        images: ['', '']
      });
      setShowAddForm(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert(t('admin.errorSavingProduct'));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    console.log('Editing product:', product); // Debug log
    setFormData({
      category: product.category || '',
      brand: product.brand || '',
      product_name: product.product_name || '',
      description: product.description || '',
      price_single: product.price_single || '',
      price_box: product.price_box || '',
      price_packet: product.price_packet || '',
      box_size: product.box_size || '',
      currency: product.currency || 'EGP',
      stock_single: product.stock_single || product.stock || '', // Fallback to old stock field
      stock_box: product.stock_box || '',
      stock_packet: product.stock_packet || '',
      images: product.images && product.images.length ? [product.images[0] || '', product.images[1] || ''] : [product.image || '', '']
    });
    setShowAddForm(true);
    
    // Scroll to form after a short delay to ensure it's rendered
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(API_ENDPOINTS.PRODUCTS.DELETE(productId), {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert(t('admin.errorDeletingProduct'));
      }
    }
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setShowAddForm(false);
    setFormData({
      category: '',
      brand: '',
      product_name: '',
      description: '',
      price_single: '',
      price_box: '',
      price_packet: '',
      box_size: '',
      currency: 'EGP',
      stock_single: '',
      stock_box: '',
      stock_packet: '',
      images: ['', '']
    });
  };

  // Enhanced filtering and sorting
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesBrand = !selectedBrand || product.brand === selectedBrand;
    
    // Price range filtering
    let matchesPrice = true;
    if (priceRange !== 'all') {
      const singlePrice = product.price_single || 0;
      const boxPrice = product.price_box || 0;
      const packetPrice = product.price_packet || 0;
      const minPrice = Math.min(
        singlePrice || Infinity,
        boxPrice || Infinity,
        packetPrice || Infinity
      );
      
      if (priceRange === '0-100') matchesPrice = minPrice >= 0 && minPrice <= 100;
      else if (priceRange === '100-500') matchesPrice = minPrice > 100 && minPrice <= 500;
      else if (priceRange === '500-1000') matchesPrice = minPrice > 500 && minPrice <= 1000;
      else if (priceRange === '1000+') matchesPrice = minPrice > 1000;
    }
    
    // Stock filtering - check if any stock type has inventory
    let matchesStock = true;
    if (stockFilter !== 'all') {
      const totalStock = (product.stock_single || 0) + (product.stock_box || 0) + (product.stock_packet || 0);
      if (stockFilter === 'in-stock') matchesStock = totalStock > 0;
      else if (stockFilter === 'out-of-stock') matchesStock = totalStock === 0;
      else if (stockFilter === 'low-stock') matchesStock = totalStock > 0 && totalStock <= 10;
    }
    
    return matchesSearch && matchesCategory && matchesBrand && matchesPrice && matchesStock;
  }).sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.product_name?.toLowerCase() || '';
        bValue = b.product_name?.toLowerCase() || '';
        break;
      case 'brand':
        aValue = a.brand?.toLowerCase() || '';
        bValue = b.brand?.toLowerCase() || '';
        break;
      case 'category':
        aValue = a.category?.toLowerCase() || '';
        bValue = b.category?.toLowerCase() || '';
        break;
      case 'price':
        aValue = Math.min(
          a.price_single || Infinity,
          a.price_box || Infinity,
          a.price_packet || Infinity
        );
        bValue = Math.min(
          b.price_single || Infinity,
          b.price_box || Infinity,
          b.price_packet || Infinity
        );
        break;
      case 'stock':
        aValue = (a.stock_single || 0) + (a.stock_box || 0) + (a.stock_packet || 0);
        bValue = (b.stock_single || 0) + (b.stock_box || 0) + (b.stock_packet || 0);
        break;
      default:
        return 0;
    }
    
    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Get all unique brands for filtering
  const allBrands = Array.from(new Set(products.map(p => p.brand).filter(Boolean)));
  const allCategories = Array.from(new Set(products.map(p => p.category).filter(Boolean)));
  
  // Form brands based on selected category
  const formBrands = formData.category ? categoryBrands[formData.category] || [] : [];
  
  // Clear all filters function
  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedBrand('');
    setPriceRange('all');
    setStockFilter('all');
    setSortBy('name');
    setSortOrder('asc');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cigar-cream flex items-center justify-center">
        <div className="text-2xl text-cigar-dark">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cigar-cream py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-cigar font-bold text-cigar-dark">{t('admin.productsManagement')}</h1>
            <p className="text-gray-600 mt-2">{t('admin.manageInventory')}</p>
          </div>
          <button
            onClick={() => {
              setShowAddForm(true);
              setEditingProduct(null);
              // Reset form data
              setFormData({
                category: '',
                brand: '',
                product_name: '',
                description: '',
                price_single: '',
                price_box: '',
                price_packet: '',
                box_size: '',
                currency: 'EGP',
                stock_single: '',
                stock_box: '',
                stock_packet: '',
                images: ['', '']
              });
              // Scroll to form
              setTimeout(() => {
                if (formRef.current) {
                  formRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start',
                    inline: 'nearest'
                  });
                }
              }, 100);
            }}
            className="mt-4 sm:mt-0 bg-cigar-brown hover:bg-cigar-dark text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
          >
            <FaPlus />
            <span>{t('admin.addProduct')}</span>
          </button>
        </div>

        {/* Add/Edit Product Form */}
        {showAddForm && (
          <div ref={formRef} className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-cigar-dark">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
              <button
                onClick={cancelEdit}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  >
                    <option value="">Select Category</option>
                    {allCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  <select
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                    disabled={!formData.category}
                  >
                    <option value="">Select Brand</option>
                    {formBrands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input
                    type="text"
                    name="product_name"
                    value={formData.product_name}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder={t('admin.enterProductName')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Single ({formData.currency})</label>
                  <input
                    type="number"
                    name="price_single"
                    value={formData.price_single}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Box ({formData.currency})</label>
                  <input
                    type="number"
                    name="price_box"
                    value={formData.price_box}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Packet ({formData.currency})</label>
                  <input
                    type="number"
                    name="price_packet"
                    value={formData.price_packet}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Box Size</label>
                  <input
                    type="number"
                    name="box_size"
                    value={formData.box_size}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="20"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  >
                    <option value="EGP">EGP</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock Single</label>
                  <input
                    type="number"
                    name="stock_single"
                    value={formData.stock_single}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock Box</label>
                  <input
                    type="number"
                    name="stock_box"
                    value={formData.stock_box}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock Packet</label>
                  <input
                    type="number"
                    name="stock_packet"
                    value={formData.stock_packet}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="input-field"
                    rows="3"
                    placeholder={t('admin.enterProductDescription')}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image URL 1</label>
                  <input
                    type="url"
                    name="image1"
                    value={formData.images[0]}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="https://example.com/image1.jpg"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image URL 2 (optional)</label>
                  <input
                    type="url"
                    name="image2"
                    value={formData.images[1]}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="https://example.com/image2.jpg"
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="bg-cigar-brown hover:bg-cigar-dark text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
                >
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Enhanced Filters and Search */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          {/* Search and Filter Toggle */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('admin.searchProductsPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10 text-sm sm:text-base"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary flex items-center justify-center space-x-2 px-4 py-2"
            >
              <FaFilter />
              <span className="hidden sm:inline">Filters</span>
            </button>
            <button
              onClick={clearAllFilters}
              className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <FaTimes />
              <span className="hidden sm:inline">Clear All</span>
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t border-gray-200 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="input-field text-sm"
                  >
                    <option value="">All Categories</option>
                    {allCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* Brand Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="input-field text-sm"
                  >
                    <option value="">All Brands</option>
                    {allBrands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>

                {/* Price Range Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Range</label>
                  <select
                    value={priceRange}
                    onChange={(e) => setPriceRange(e.target.value)}
                    className="input-field text-sm"
                  >
                    <option value="all">All Prices</option>
                    <option value="0-100">EGP 0 - 100</option>
                    <option value="100-500">EGP 100 - 500</option>
                    <option value="500-1000">EGP 500 - 1,000</option>
                    <option value="1000+">EGP 1,000+</option>
                  </select>
                </div>

                {/* Stock Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock Status</label>
                  <select
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                    className="input-field text-sm"
                  >
                    <option value="all">All Stock</option>
                    <option value="in-stock">In Stock</option>
                    <option value="out-of-stock">Out of Stock</option>
                    <option value="low-stock">Low Stock (≤10)</option>
                  </select>
                </div>
              </div>

              {/* Sort Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="input-field text-sm"
                  >
                    <option value="name">Product Name</option>
                    <option value="brand">Brand</option>
                    <option value="category">Category</option>
                    <option value="price">Price</option>
                    <option value="stock">Stock</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="input-field text-sm"
                  >
                    <option value="asc">Ascending (A-Z)</option>
                    <option value="desc">Descending (Z-A)</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products Table */}
        <div ref={tableRef} className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-base sm:text-lg font-semibold text-cigar-dark">
                Products ({filteredProducts.length} of {products.length})
            </h3>
              {filteredProducts.length !== products.length && (
                <div className="mt-2 sm:mt-0">
                  <span className="text-sm text-gray-500">
                    Showing filtered results
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <FaBoxes className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No products found</h3>
              <p className="text-gray-500">Try adjusting your search criteria or add a new product.</p>
            </div>
          ) : (
            <div className="overflow-x-auto mobile-scroll">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pricing
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.map((product) => (
                    <tr key={product._id} className="hover:bg-gray-50">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="w-full h-32 flex justify-center items-center">
                          {product.images && product.images.length === 2 ? (
                            product.images.map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={product.product_name}
                                className="w-1/2 h-full object-cover m-0 p-0 border-0 rounded-none"
                              />
                            ))
                          ) : (
                            <img
                              src={(product.images && product.images[0]) || product.image || 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=100&h=160&fit=crop'}
                              alt={product.product_name}
                              className="w-full h-full object-cover m-0 p-0 border-0 rounded-none"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="bg-cigar-gold text-cigar-dark px-2 py-1 rounded-full text-xs font-semibold">
                          {product.brand || 'N/A'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="bg-cigar-brown text-cigar-cream px-2 py-1 rounded-full text-xs font-semibold">
                          {product.category || 'N/A'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        <div className="space-y-1">
                          {product.price_single && product.price_single > 0 && (
                            <div>Single: {product.currency} {product.price_single}</div>
                          )}
                          {product.price_box && product.price_box > 0 && (
                            <div>Box: {product.currency} {product.price_box}</div>
                          )}
                          {product.price_packet && product.price_packet > 0 && (
                            <div>Packet: {product.currency} {product.price_packet}</div>
                          )}
                          {(!product.price_single || product.price_single <= 0) && 
                           (!product.price_box || product.price_box <= 0) && 
                           (!product.price_packet || product.price_packet <= 0) && (
                            <div className="text-gray-500 italic">No pricing set</div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">Single:</span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (product.stock_single || 0) === 0 
                                ? 'bg-red-100 text-red-800' 
                                : (product.stock_single || 0) <= 5 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {product.stock_single || 0}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">Box:</span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (product.stock_box || 0) === 0 
                                ? 'bg-red-100 text-red-800' 
                                : (product.stock_box || 0) <= 5 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {product.stock_box || 0}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">Packet:</span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (product.stock_packet || 0) === 0 
                                ? 'bg-red-100 text-red-800' 
                                : (product.stock_packet || 0) <= 5 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {product.stock_packet || 0}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-1 sm:space-x-2">
                          <button
                            onClick={() => handleEdit(product)}
                            className="text-cigar-brown hover:text-cigar-dark p-1 sm:p-2 rounded transition-colors"
                            title={t('admin.editProduct')}
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(product._id)}
                            className="text-red-500 hover:text-red-700 p-1 sm:p-2 rounded transition-colors"
                            title={t('admin.deleteProduct')}
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
